/**
 * ItineraryView — single canonical surface for everything the visitor's
 * planning. Replaces both the old empty Itinerary stub AND the legacy
 * Bookings tab (which was redundant — confirmed bookings live here too,
 * filtered by status).
 *
 * Filter partitions:
 *   All • Saved • To Pay • Confirmed
 *
 * Each row shows: image, title, date/party summary, status pill. Pending-
 * pay rows render a primary "Pay now" CTA that hands the item to the
 * checkout view (which iframes the partner page + runs useIframeInspector).
 */
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useItinerary,
  type ItineraryItem,
  type ItineraryItemStatus,
  type ItineraryItemPriority,
} from '../context/ItineraryContext';
import { useView } from '../context/ViewContext';
import { useCheckout } from '../context/CheckoutContext';
import { useInstance } from '../context/InstanceContext';
import { emit as emitTelemetry } from '../utils/telemetry';
import { useChatSession } from '../context/ChatSessionContext';
import { useParentBridge } from '../context/ParentBridgeContext';
import TripProfileCard from '../components/TripProfileCard';
import CollaboratorsBar from '../components/CollaboratorsBar';
import TripSwitcher from '../components/TripSwitcher';

type FilterId = 'all' | 'saved' | 'pending' | 'confirmed';

const FILTERS: {
  id: FilterId;
  i18nKey: string;
  match: (s: ItineraryItemStatus) => boolean;
}[] = [
  { id: 'all', i18nKey: 'itinerary.filter_all', match: () => true },
  {
    id: 'saved',
    i18nKey: 'itinerary.filter_saved',
    match: s => s === 'suggested' || s === 'saved',
  },
  {
    id: 'pending',
    i18nKey: 'itinerary.filter_pending',
    match: s => s === 'pending_pay',
  },
  {
    id: 'confirmed',
    i18nKey: 'itinerary.filter_confirmed',
    match: s => s === 'confirmed',
  },
];

const formatDate = (iso: string | undefined, locale: string) => {
  if (!iso) return '';
  try {
    const d = new Date(iso + 'T12:00:00');
    return new Intl.DateTimeFormat(locale, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    }).format(d);
  } catch {
    return iso;
  }
};

const STATUS_I18N_KEY: Record<ItineraryItemStatus, string> = {
  suggested: 'itinerary.status_suggested',
  saved: 'itinerary.status_saved',
  pending_pay: 'itinerary.status_pending_pay',
  confirmed: 'itinerary.status_confirmed',
  cancelled: 'itinerary.status_cancelled',
};

const ItineraryView: React.FC = () => {
  const { t } = useTranslation();
  const {
    items,
    counts,
    startCheckout,
    removeItem,
    updateItem,
    tripProfile,
    title,
    renameItinerary,
    myUserId,
    myRole,
    members,
    setItemScope,
  } = useItinerary();

  const canEdit = myRole === 'owner' || myRole === 'editor';

  // added_by → display label for the personal-pick badge. "You" for the current
  // visitor, else the member's name, else a generic fallback.
  const nameFor = (userId?: string): string => {
    if (!userId) return '';
    if (userId === myUserId) return t('collab.you');
    const m = members.find(x => x.user_id === userId);
    return m?.name || t('collab.someone');
  };

  // Toggle emphasis: clicking the active level clears it (→ plain saved).
  const setPriority = (item: ItineraryItem, level: ItineraryItemPriority) => {
    updateItem(item.id, {
      priority: item.priority === level ? undefined : level,
    });
  };
  const { setCart } = useCheckout();
  const { setView } = useView();
  const { chatSessionId } = useChatSession();
  const { effectiveLocale } = useInstance();
  const [filter, setFilter] = useState<FilterId>('all');
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');

  const beginEditTitle = () => {
    setTitleDraft(title);
    setEditingTitle(true);
  };
  const commitTitle = () => {
    setEditingTitle(false);
    if (titleDraft.trim() && titleDraft.trim() !== title) {
      renameItinerary(titleDraft);
    }
  };

  const visible = useMemo(() => {
    const f = FILTERS.find(x => x.id === filter)!;
    return items.filter(it => f.match(it.status));
  }, [items, filter]);

  const parentBridge = useParentBridge();
  // Deep-integration hosts (explore map) highlight the matching pin while
  // a trip row is hovered. Title is the join key — item-level metadata
  // isn't persisted. No-op for hosts that don't listen.
  const emitHover = (title: string | null) => {
    parentBridge.send({
      type: 'wg:event',
      payload: { event_type: 'itinerary_hover', title },
    });
  };

  const onPay = (item: ItineraryItem) => {
    const cart = startCheckout(item.id);
    if (!cart) return;
    setCart(cart);
    emitTelemetry('itinerary_pay_clicked', {
      sessionId: chatSessionId || undefined,
      props: {
        item_id: item.id,
        provider: item.provider,
        activity_id: item.activity_id,
        booking_method: item.booking_method,
      },
    });
    setView('checkout');
  };

  if (items.length === 0) {
    return (
      <div
        className="wg-itin wg-itin--empty"
        role="button"
        tabIndex={0}
        aria-label={t('nav.trip')}
        onClick={() => setView('chat')}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setView('chat');
          }
        }}
      >
        <div className="wg-itin__empty-icon" aria-hidden>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="3" y="4" width="18" height="18" rx="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
        </div>
      </div>
    );
  }

  return (
    <div className="wg-itin">
      <TripSwitcher />
      <header className="wg-itin__header">
        {editingTitle ? (
          <input
            className="wg-itin__title-input"
            value={titleDraft}
            autoFocus
            maxLength={120}
            aria-label={t('itinerary.rename_label')}
            onChange={e => setTitleDraft(e.target.value)}
            onBlur={commitTitle}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commitTitle();
              } else if (e.key === 'Escape') {
                e.preventDefault();
                setEditingTitle(false);
              }
            }}
          />
        ) : (
          <button
            type="button"
            className="wg-itin__title wg-itin__title--editable"
            title={t('itinerary.rename_label')}
            aria-label={t('itinerary.rename_label')}
            onClick={beginEditTitle}
          >
            {title}
            <svg
              className="wg-itin__title-edit-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <path d="M12 20h9" />
              <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
            </svg>
          </button>
        )}
        <div className="wg-itin__filters" role="tablist">
          {FILTERS.map(f => {
            const c =
              f.id === 'all'
                ? items.length
                : f.id === 'saved'
                  ? counts.saved + counts.suggested
                  : f.id === 'pending'
                    ? counts.pending_pay
                    : counts.confirmed;
            return (
              <button
                key={f.id}
                type="button"
                role="tab"
                aria-selected={filter === f.id}
                className={`wg-itin__filter${filter === f.id ? ' is-active' : ''}`}
                onClick={() => setFilter(f.id)}
              >
                {t(f.i18nKey)}
                {c > 0 && <span className="wg-itin__filter-count">{c}</span>}
              </button>
            );
          })}
        </div>
      </header>

      <CollaboratorsBar />

      <TripProfileCard />

      <ul className="wg-itin__list" role="list">
        {visible.length === 0 && (
          <li className="wg-itin__list-empty">
            {t('itinerary.list_empty_for_filter')}
          </li>
        )}
        {visible.map(item => (
          <li
            key={item.id}
            className="wg-itin__row"
            data-status={item.status}
            data-priority={item.priority || undefined}
            onMouseEnter={() => emitHover(item.title)}
            onMouseLeave={() => emitHover(null)}
          >
            {item.image_url ? (
              <img
                className="wg-itin__row-image"
                src={item.image_url}
                alt=""
                loading="lazy"
              />
            ) : (
              <div
                className="wg-itin__row-image wg-itin__row-image--placeholder"
                aria-hidden
              />
            )}
            <div className="wg-itin__row-body">
              <div className="wg-itin__row-title-line">
                <h3 className="wg-itin__row-title">{item.title}</h3>
                {item.scope === 'personal' && (
                  <span className="wg-itin__row-personal">
                    {t('collab.pick_by', { name: nameFor(item.added_by) })}
                  </span>
                )}
                <span
                  className={`wg-itin__row-status wg-itin__row-status--${item.status}`}
                >
                  {t(STATUS_I18N_KEY[item.status])}
                </span>
              </div>
              {item.description && (
                <p className="wg-itin__row-desc">{item.description}</p>
              )}
              <div className="wg-itin__row-meta">
                {item.date && (
                  <span>{formatDate(item.date, effectiveLocale)}</span>
                )}
                {item.guests && item.guests.length > 0 && (
                  <span>
                    {item.guests
                      .map(g =>
                        t('itinerary.guest_count', {
                          count: g.count,
                          label: g.type.split(' ')[0],
                        }),
                      )
                      .join(' · ')}
                  </span>
                )}
                {typeof item.unit_price_cents === 'number' &&
                  (() => {
                    // Resolve party: confirmed item.guests > tripProfile
                    // > brain default (2 adults). Same fallback as the
                    // Pay tab so the row total stays consistent across
                    // surfaces — visitor sees the same number wherever
                    // they look.
                    const itemTotal = (item.guests || []).reduce(
                      (s, g) => s + (g.count || 0),
                      0,
                    );
                    const profileTotal =
                      (tripProfile.party?.adults || 0) +
                      (tripProfile.party?.children || 0);
                    const totalCount =
                      itemTotal > 0
                        ? itemTotal
                        : profileTotal > 0
                          ? profileTotal
                          : 2;
                    const currency = item.currency_code || 'USD';
                    // Show real cents end-to-end — pp + total stay in
                    // the same precision so we don't render "$230 pp"
                    // alongside a $460.74 total and look broken.
                    const fmt = (cents: number) =>
                      new Intl.NumberFormat(effectiveLocale, {
                        style: 'currency',
                        currency,
                      }).format(cents / 100);
                    return (
                      <>
                        <span className="wg-itin__row-total">
                          {fmt(item.unit_price_cents * totalCount)}
                        </span>
                        <span className="wg-itin__row-pp">
                          {fmt(item.unit_price_cents)} pp · {totalCount}{' '}
                          {totalCount === 1 ? 'guest' : 'guests'}
                        </span>
                      </>
                    );
                  })()}
                {item.confirmation_code && (
                  <span className="wg-itin__row-code">
                    {t('itinerary.confirmation_prefix')}
                    {item.confirmation_code}
                  </span>
                )}
              </div>
              <div className="wg-itin__row-actions">
                {item.status !== 'confirmed' && (
                  <div
                    className="wg-itin__row-priority"
                    role="group"
                    aria-label={t('itinerary.priority_must_do_aria')}
                  >
                    <button
                      type="button"
                      className={`wg-itin__priority-btn wg-itin__priority-btn--must${
                        item.priority === 'must_do' ? ' is-active' : ''
                      }`}
                      aria-pressed={item.priority === 'must_do'}
                      aria-label={t('itinerary.priority_must_do_aria')}
                      onClick={() => setPriority(item, 'must_do')}
                    >
                      <svg
                        viewBox="0 0 24 24"
                        aria-hidden
                        fill={
                          item.priority === 'must_do' ? 'currentColor' : 'none'
                        }
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M12 17.3 6.2 20l1.1-6.4L2.6 9l6.4-.9L12 2.3l3 5.8 6.4.9-4.7 4.6L17.8 20z" />
                      </svg>
                      {t('itinerary.priority_must_do')}
                    </button>
                    {/* "Maybe" was removed from the UI (2026-07-20): Must-do is a
                        single toggle, and a second lukewarm state added choice
                        without adding signal. The 'maybe' value is still accepted
                        by the API and the DB CHECK constraint, so any existing rows
                        keep rendering their stored priority — we simply no longer
                        offer it as a way to SET one. */}
                  </div>
                )}
                {/* Scope toggle — only on a real collaboration (more than the
                    owner) or an already-personal item, so solo trips stay
                    uncluttered. Editors + owner only. */}
                {canEdit &&
                  item.status !== 'confirmed' &&
                  (members.length > 1 || item.scope === 'personal') && (
                    <button
                      type="button"
                      className="wg-itin__row-scope"
                      onClick={() =>
                        setItemScope(
                          item.id,
                          item.scope === 'personal' ? 'group' : 'personal',
                        )
                      }
                    >
                      {item.scope === 'personal'
                        ? t('collab.promote_group')
                        : t('collab.make_personal')}
                    </button>
                  )}
                {item.status === 'pending_pay' && item.embedded_url && (
                  <button
                    type="button"
                    className="wg-itin__row-pay"
                    onClick={() => onPay(item)}
                  >
                    {t('itinerary.row_pay_now')}
                  </button>
                )}
                {(item.status === 'suggested' || item.status === 'saved') &&
                  item.embedded_url && (
                    <button
                      type="button"
                      className="wg-itin__row-pay"
                      onClick={() => onPay(item)}
                    >
                      {t('itinerary.row_reserve')}
                    </button>
                  )}
                {item.status !== 'confirmed' && (
                  <button
                    type="button"
                    className="wg-itin__row-remove"
                    onClick={() => removeItem(item.id)}
                  >
                    {t('common.remove')}
                  </button>
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default ItineraryView;
